package uk.co.nickthecoder.kyd.util

import uk.co.nickthecoder.glok.property.boilerplate.intProperty
import uk.co.nickthecoder.glok.util.currentTimeMillis
import uk.co.nickthecoder.glok.util.log
import uk.co.nickthecoder.kyd.*

/**
 * Calls `tick` related methods every frame.
 * The order of operations :
 *
 * 1. [Management.preTick]
 * 2. [Director.preTick]
 * 3. [Behaviour.tick]
 * 2. [Director.preTick]
 * 1. [Management.preTick]
 *
 * Note. [Behaviour.tick] is only called for top-level [Actor]s.
 * i.e. those whose parent is a [Stage].
 * Child actors (whose parent is another [Actor]) are NOT called.
 * If child actors need to be ticked, then this is the responsibility of the parent actor's behaviour. e.g.
 *
 *     class MyParentBehaviour : Behaviour {
 *         override fun tick( actor : Actor, seconds : Float ) {
 *             // Actions to perform before the children...
 *
 *             for (child in actor.children) {
 *                 child.behaviour.tick( child, seconds )
 *             }
 *
 *             // Actions to perform after the children...
 *         }
 *     }
 */
object Ticker {

    val tickCounterProperty by intProperty(0)
    var tickCounter by tickCounterProperty

    /**
     * A timestamp (in milliseconds since the epoch) of the current tick.
     * i.e. this remains fixed for an entire frame, and then jumps 1/60th of a second
     * when the next tick happens.
     *
     * Note, this is a Double, and not a Long, because Javascript doesn't support Longs.
     */
    var now: Double = currentTimeMillis() - 1000.0 / 60.0
        private set

    /**
     * A timestamp (in milliseconds since the epoch) of the previous tick.
     *
     * [now] - [previousTick] is the elapsed time for the current tick, which is passed to
     * [Behaviour.tick].
     */
    var previousTick = now
        private set

    private val toTickMap = mutableMapOf<Stage, MutableList<Actor>>()


    fun reset() {
        tickCounter = 0
        // Ensure the first tick of the Act has a "sensible" elapse time.
        // Without the constant, the elapse time would be very close to zero.
        now = currentTimeMillis()
        previousTick = now - 1000.0 / 60.0
    }

    fun tick(act: Act) {
        now = currentTimeMillis()
        val seconds = (now - previousTick).toFloat() / 1000f

        try {
            Management.instance.preTick(seconds)
        } catch (e: Exception) {
            log.severe("Management threw exception during preTick.")
            log.severe(e)
        }
        try {
            act.director.preTick(seconds)
        } catch (e: Exception) {
            log.severe("Director ${act.director} threw exception during preTick.")
            log.severe(e)
        }

        // Collect all behaviours that should be "ticked"
        // By collecting them first, and ticking later, we prevent problems :
        // A behaviour will only tick once (even if the actor jumps from one arena to another)
        // We avoid exceptions while iterating when the collection changes.
        // However, this may cause other issues :
        // If one actor's tick "kills" another actor, the 2nd actor may still tick.
        // The simplest solution : Set the actor's behaviour to NoBehaviour when it is "killed".
        // In a polished game, "death" isn't instantaneous, and usually its behaviour is replaced by
        // a "fade" animation, and the actor is only removed from the Arena when the fade completes.
        for (stage in act.stages) {
            val toTick = mutableListOf<Actor>()
            for (actor in stage.actors) {
                val behaviour = actor.behaviour
                // Skip over passive objects, such as backgrounds / decorations.
                if (behaviour !== NoBehaviour) {
                    toTick.add(actor)
                }
            }
            if (toTick.isNotEmpty()) {
                toTickMap[stage] = toTick
            }
        }

        for (toTick in toTickMap.values) {
            for (actor in toTick) {
                try {
                    actor.behaviour.tick(actor, seconds)
                } catch (e: Exception) {
                    log.severe("Behaviour ${actor.behaviour} threw exception during tick. Actor removed.")
                    log.severe(e)
                    actor.parent.removeActor(actor)
                }
            }
        }

        toTickMap.clear()

        try {
            act.director.postTick(seconds)
        } catch (e: Exception) {
            log.severe("Director ${act.director} threw exception during postTick.")
            log.severe(e)
        }
        try {
            Management.instance.postTick(seconds)
        } catch (e: Exception) {
            log.severe("Management threw exception during postTick.")
            log.severe(e)
        }

        previousTick = now
        tickCounter ++
    }

}
